import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Plus, Search, Edit, Trash2, Archive, ArchiveRestore, RefreshCw,
    Download, FileText, FilePlus, Files, X,
    ArrowUpDown, ChevronUp, ChevronDown, FolderCog, Check, Pencil,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Header } from '../../components/Header';

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_TYPES = [
    { value: 'vinyl', label: 'Vinyl' },
    { value: 'cd', label: 'CD' },
    { value: 'dvd', label: 'DVD' },
    { value: 'bluray', label: 'Blu-ray' },
];

const ACCEPT = '.pdf,.zip,.ai,.eps,.indd,.psd,.png,.jpg,.jpeg,.svg';

const emptyForm = {
    name: '',
    description: '',
    product_type: 'vinyl',
    category: '',
    subcategory: '',
    is_active: true,
    files: [],
    preview: null,
    // Set to true when the admin has clicked "Remove" on an existing
    // preview image while editing — tells the PUT endpoint to drop it.
    remove_preview: false,
};

const fmtBytes = (n) => {
    if (!n && n !== 0) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export default function ArtworkTemplatesAdminPage() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [productFilter, setProductFilter] = useState('');
    const [showArchived, setShowArchived] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    // Signed URL of the existing preview (loaded when opening the edit
    // dialog) so admins can see the current thumbnail before deciding to
    // replace or remove it. Null on create OR when the template has no
    // preview yet.
    const [existingPreviewUrl, setExistingPreviewUrl] = useState(null);
    const [isDraggingFiles, setIsDraggingFiles] = useState(false);
    const [isDraggingPreview, setIsDraggingPreview] = useState(false);

    // Manage-files dialog state
    const [filesFor, setFilesFor] = useState(null); // template being managed
    const [addFile, setAddFile] = useState(null);
    const [addLabel, setAddLabel] = useState('');
    const [busyFileId, setBusyFileId] = useState(null);

    // SKU multi-select state (Linked Sales SKUs section)
    const [skuOptions, setSkuOptions] = useState([]);
    const [skuLoading, setSkuLoading] = useState(false);
    const [selectedSkuIds, setSelectedSkuIds] = useState(new Set());
    const [skuFilterProduct, setSkuFilterProduct] = useState('');
    const [skuFilterCategory, setSkuFilterCategory] = useState('');
    const [skuSearch, setSkuSearch] = useState('');

    // Category filter (depends on productFilter) + sorting
    const [categoryFilter, setCategoryFilter] = useState('');
    const [subcategoryFilter, setSubcategoryFilter] = useState('');
    const [sortKey, setSortKey] = useState('updated_at');
    const [sortDir, setSortDir] = useState('desc');

    // Persistent categories: for the template form (by form.product_type)
    // and for the main-list category filter (by productFilter).
    const [formCategories, setFormCategories] = useState([]);
    const [filterCategories, setFilterCategories] = useState([]);

    // Manage Categories dialog
    const [manageCatOpen, setManageCatOpen] = useState(false);
    const [manageProduct, setManageProduct] = useState('vinyl');
    const [manageCats, setManageCats] = useState([]);
    const [manageCatLoading, setManageCatLoading] = useState(false);
    const [newCatName, setNewCatName] = useState('');
    const [editingCatId, setEditingCatId] = useState(null);
    const [editingCatName, setEditingCatName] = useState('');
    const [catBusy, setCatBusy] = useState(false);

    const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (showArchived) params.set('include_archived', 'true');
            if (productFilter) params.set('product_type', productFilter);
            const { data } = await axios.get(`${API}/api/artwork-templates/admin?${params}`, { headers: authHeaders() });
            setTemplates(data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load templates');
        } finally {
            setLoading(false);
        }
    }, [productFilter, showArchived]);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    // Keep the "Manage files" dialog in sync with the latest server state
    // so newly uploaded/deleted files are reflected immediately.
    useEffect(() => {
        if (!filesFor) return;
        const fresh = templates.find(t => t.id === filesFor.id);
        if (fresh) setFilesFor(fresh);
    }, [templates]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const sortValue = (t) => {
        switch (sortKey) {
            case 'name': return (t.name || '').toLowerCase();
            case 'product_type': return (t.product_type || '').toLowerCase();
            case 'category': return (t.category || '').toLowerCase();
            case 'subcategory': return (t.subcategory || '').toLowerCase();
            case 'files': return (t.files || []).length;
            case 'size': return (t.files || []).reduce((s, f) => s + (f.size || 0), 0);
            case 'status': return t.is_archived ? 2 : (t.is_active ? 0 : 1);
            default: return t.updated_at || '';
        }
    };

    const displayed = (() => {
        const s = search.toLowerCase();
        const list = templates.filter(t => {
            if (categoryFilter && (t.category || '') !== categoryFilter) return false;
            if (subcategoryFilter && (t.subcategory || '') !== subcategoryFilter) return false;
            if (!search) return true;
            return (
                (t.name || '').toLowerCase().includes(s) ||
                (t.description || '').toLowerCase().includes(s) ||
                (t.category || '').toLowerCase().includes(s) ||
                (t.subcategory || '').toLowerCase().includes(s)
            );
        });
        const dir = sortDir === 'asc' ? 1 : -1;
        list.sort((a, b) => {
            const va = sortValue(a), vb = sortValue(b);
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            // Stable tie-breaker so order is deterministic across reloads.
            return (a.name || '').localeCompare(b.name || '');
        });
        return list;
    })();

    // Subcategory filter options depend on the current Product Type
    // (already applied server-side via productFilter) and the selected
    // Category. Derived from the loaded templates — subcategories are free
    // text on templates, not a managed collection.
    const subcategoryOptions = (() => {
        const set = new Set();
        templates.forEach(t => {
            if (categoryFilter && (t.category || '') !== categoryFilter) return;
            if (t.subcategory) set.add(t.subcategory);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    })();

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setExistingPreviewUrl(null);
        setSelectedSkuIds(new Set());
        setSkuFilterProduct('vinyl');
        setSkuFilterCategory('');
        setSkuSearch('');
        loadSkuOptions();
        setDialogOpen(true);
    };
    const openEdit = (tpl) => {
        setEditing(tpl);
        setForm({
            name: tpl.name || '',
            description: tpl.description || '',
            product_type: tpl.product_type || 'vinyl',
            category: tpl.category || '',
            subcategory: tpl.subcategory || '',
            is_active: !!tpl.is_active,
            files: [],
            preview: null,
            remove_preview: false,
        });
        // Fetch a signed preview URL if the template has one so the
        // admin sees the current thumbnail. `preview_filename` acts as
        // the flag — it is only populated when a real preview upload
        // (not an auto-derived file preview) has been attached.
        if (tpl.preview_filename) {
            axios
                .get(`${API}/api/artwork-templates/${tpl.id}/preview`, { headers: authHeaders() })
                .then(({ data }) => setExistingPreviewUrl(data?.preview_url || null))
                .catch(() => setExistingPreviewUrl(null));
        } else {
            setExistingPreviewUrl(null);
        }
        setSkuFilterProduct(tpl.product_type || '');
        setSkuFilterCategory('');
        setSkuSearch('');
        loadSkuOptions(tpl.id);
        setDialogOpen(true);
    };

    const loadSkuOptions = async (preselectForTemplateId = null) => {
        setSkuLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/artwork-templates/admin/sku-options`, { headers: authHeaders() });
            setSkuOptions(data || []);
            if (preselectForTemplateId) {
                const pre = new Set(
                    (data || [])
                        .filter(s => (s.linked_template_ids || []).includes(preselectForTemplateId))
                        .map(s => s.id)
                );
                setSelectedSkuIds(pre);
            } else {
                setSelectedSkuIds(new Set());
            }
        } catch (e) {
            toast.error('Failed to load Sales SKUs');
        } finally {
            setSkuLoading(false);
        }
    };

    // When product_type changes on the template form, follow it in the SKU
    // filter to save the admin a click. Keep the current filter if the user
    // has explicitly changed it (i.e. product filter still matches previous
    // template.product_type).
    useEffect(() => {
        if (!dialogOpen) return;
        setSkuFilterProduct(form.product_type || '');
        setSkuFilterCategory('');
        loadFormCategories(form.product_type);
    }, [form.product_type, dialogOpen]);

    // --- Persistent category loaders -----------------------------------
    const loadFormCategories = async (ptype) => {
        try {
            const { data } = await axios.get(
                `${API}/api/artwork-templates/admin/categories?product_type=${ptype}`,
                { headers: authHeaders() },
            );
            setFormCategories(data || []);
        } catch { setFormCategories([]); }
    };

    const loadFilterCategories = useCallback(async () => {
        try {
            const qp = productFilter ? `?product_type=${productFilter}` : '';
            const { data } = await axios.get(
                `${API}/api/artwork-templates/admin/categories${qp}`,
                { headers: authHeaders() },
            );
            // When viewing all product types the same category name can
            // appear under several families — dedupe by name for the filter.
            const seen = new Set();
            const unique = (data || []).filter(c => {
                if (seen.has(c.name)) return false;
                seen.add(c.name);
                return true;
            });
            setFilterCategories(unique);
        } catch { setFilterCategories([]); }
    }, [productFilter]);

    useEffect(() => { loadFilterCategories(); setCategoryFilter(''); setSubcategoryFilter(''); }, [loadFilterCategories]);
    useEffect(() => { setSubcategoryFilter(''); }, [categoryFilter]);

    // --- Manage Categories dialog --------------------------------------
    const loadManageCats = async (ptype) => {
        setManageCatLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/api/artwork-templates/admin/categories?product_type=${ptype}&include_archived=true`,
                { headers: authHeaders() },
            );
            setManageCats(data || []);
        } catch { toast.error('Failed to load categories'); }
        finally { setManageCatLoading(false); }
    };

    const openManageCats = () => {
        const p = productFilter || 'vinyl';
        setManageProduct(p);
        setNewCatName('');
        setEditingCatId(null);
        setManageCatOpen(true);
        loadManageCats(p);
    };

    // Refresh the form/filter pickers after category changes so new or
    // renamed categories appear immediately everywhere.
    const refreshCategoryPickers = () => {
        loadFilterCategories();
        if (dialogOpen) loadFormCategories(form.product_type);
    };

    const createCat = async () => {
        const name = newCatName.trim();
        if (!name) return;
        setCatBusy(true);
        try {
            await axios.post(
                `${API}/api/artwork-templates/admin/categories`,
                { product_type: manageProduct, name },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
            );
            setNewCatName('');
            await loadManageCats(manageProduct);
            refreshCategoryPickers();
            toast.success('Category added');
        } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
        finally { setCatBusy(false); }
    };

    const saveRenameCat = async (id) => {
        const name = editingCatName.trim();
        if (!name) return;
        setCatBusy(true);
        try {
            await axios.put(
                `${API}/api/artwork-templates/admin/categories/${id}`,
                { name },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
            );
            setEditingCatId(null);
            setEditingCatName('');
            await loadManageCats(manageProduct);
            await fetchTemplates();
            refreshCategoryPickers();
            toast.success('Category renamed');
        } catch (e) { toast.error(e.response?.data?.detail || 'Rename failed'); }
        finally { setCatBusy(false); }
    };

    const archiveCatToggle = async (cat) => {
        try {
            const endpoint = cat.is_archived ? 'unarchive' : 'archive';
            await axios.post(`${API}/api/artwork-templates/admin/categories/${cat.id}/${endpoint}`, null, { headers: authHeaders() });
            await loadManageCats(manageProduct);
            refreshCategoryPickers();
        } catch { toast.error('Action failed'); }
    };

    const deleteCat = async (cat) => {
        if (!window.confirm(`Delete category "${cat.name}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/api/artwork-templates/admin/categories/${cat.id}`, { headers: authHeaders() });
            await loadManageCats(manageProduct);
            refreshCategoryPickers();
            toast.success('Category deleted');
        } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    };

    const moveCat = async (idx, dir) => {
        const arr = [...manageCats];
        const j = idx + dir;
        if (j < 0 || j >= arr.length) return;
        [arr[idx], arr[j]] = [arr[j], arr[idx]];
        setManageCats(arr);
        try {
            await axios.post(
                `${API}/api/artwork-templates/admin/categories/reorder`,
                { product_type: manageProduct, ordered_ids: arr.map(c => c.id) },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
            );
            refreshCategoryPickers();
        } catch { toast.error('Reorder failed'); loadManageCats(manageProduct); }
    };

    // Case-insensitive family comparison. SKUs that don't have a
    // `product_family` set (legacy rows / freshly imported SKUs) are
    // treated as "unclassified" and show up under every family filter
    // so the admin can still link them. Otherwise a production DB where
    // nobody has back-filled product_family would look empty.
    const familyMatches = (s) => {
        if (!skuFilterProduct) return true;
        const sf = String(s.product_family || '').trim().toLowerCase();
        if (!sf) return true;
        return sf === skuFilterProduct.trim().toLowerCase();
    };

    // Available categories derived from the currently filtered SKU set
    const skuCategoryOptions = (() => {
        const set = new Set();
        for (const s of skuOptions) {
            if (!familyMatches(s)) continue;
            if (s.category) set.add(s.category);
        }
        return Array.from(set).sort();
    })();

    const filteredSkus = skuOptions.filter(s => {
        if (!familyMatches(s)) return false;
        if (skuFilterCategory && s.category !== skuFilterCategory) return false;
        if (skuSearch) {
            const q = skuSearch.toLowerCase();
            const hay = `${s.sales_sku} ${s.display_name || ''} ${s.category || ''}`.toLowerCase();
            if (!hay.includes(q)) return false;
        }
        return true;
    });

    const allFilteredSelected = filteredSkus.length > 0 && filteredSkus.every(s => selectedSkuIds.has(s.id));

    const toggleSku = (id) => {
        setSelectedSkuIds(prev => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };
    const toggleSelectAllFiltered = () => {
        setSelectedSkuIds(prev => {
            const n = new Set(prev);
            if (allFilteredSelected) {
                filteredSkus.forEach(s => n.delete(s.id));
            } else {
                filteredSkus.forEach(s => n.add(s.id));
            }
            return n;
        });
    };
    const removeSelected = (id) => {
        setSelectedSkuIds(prev => {
            const n = new Set(prev);
            n.delete(id);
            return n;
        });
    };
    const clearSelected = () => setSelectedSkuIds(new Set());

    const handleSubmit = async () => {
        if (!form.name.trim()) { toast.error('Name is required'); return; }
        setSaving(true);
        try {
            let templateId;
            if (editing) {
                const fd = new FormData();
                fd.append('name', form.name);
                fd.append('description', form.description || '');
                fd.append('product_type', form.product_type);
                fd.append('category', form.category || '');
                fd.append('subcategory', form.subcategory || '');
                fd.append('is_active', String(form.is_active));
                // Preview: send replacement OR removal flag. If both a
                // new upload AND remove_preview are set, the upload wins
                // (backend implicitly replaces the old one).
                if (form.preview) {
                    fd.append('preview', form.preview);
                } else if (form.remove_preview) {
                    fd.append('remove_preview', 'true');
                }
                await axios.put(`${API}/api/artwork-templates/admin/${editing.id}`, fd, { headers: authHeaders() });
                templateId = editing.id;

                // Any additional template files the admin staged in the
                // dialog get uploaded via the existing single-file endpoint
                // so we don't need to widen the PUT signature.
                for (const f of form.files) {
                    const one = new FormData();
                    one.append('file', f);
                    await axios.post(`${API}/api/artwork-templates/admin/${editing.id}/files`, one, { headers: authHeaders() });
                }
            } else {
                const fd = new FormData();
                fd.append('name', form.name);
                if (form.description) fd.append('description', form.description);
                fd.append('product_type', form.product_type);
                if (form.category) fd.append('category', form.category);
                if (form.subcategory) fd.append('subcategory', form.subcategory);
                fd.append('is_active', String(form.is_active));
                if (form.files.length > 0) fd.append('file', form.files[0]);
                if (form.preview) fd.append('preview', form.preview);
                const { data: created } = await axios.post(`${API}/api/artwork-templates/admin`, fd, { headers: authHeaders() });
                templateId = created.id;

                for (let i = 1; i < form.files.length; i++) {
                    const f = form.files[i];
                    const one = new FormData();
                    one.append('file', f);
                    await axios.post(`${API}/api/artwork-templates/admin/${created.id}/files`, one, { headers: authHeaders() });
                }
            }

            // Sync linked SKUs (many-to-many). Runs for both create and edit;
            // for create the initial state was empty so this is a no-op unless
            // the admin picked some SKUs in the dialog.
            const skuIds = Array.from(selectedSkuIds);
            await axios.put(
                `${API}/api/artwork-templates/admin/${templateId}/skus`,
                { sku_item_ids: skuIds },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
            );

            toast.success(
                editing
                    ? `Template updated (${skuIds.length} SKU link${skuIds.length === 1 ? '' : 's'})`
                    : `Template created (${form.files.length} file${form.files.length === 1 ? '' : 's'}, ${skuIds.length} SKU link${skuIds.length === 1 ? '' : 's'})`
            );
            setDialogOpen(false);
            fetchTemplates();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleArchiveToggle = async (tpl) => {
        try {
            const endpoint = tpl.is_archived ? 'unarchive' : 'archive';
            await axios.post(`${API}/api/artwork-templates/admin/${tpl.id}/${endpoint}`, null, { headers: authHeaders() });
            toast.success(tpl.is_archived ? 'Template restored' : 'Template archived');
            fetchTemplates();
        } catch (e) {
            toast.error('Action failed');
        }
    };

    const handleDelete = async (tpl) => {
        if (!window.confirm(`Delete "${tpl.name}" permanently? Any SKU links will be removed. This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/api/artwork-templates/admin/${tpl.id}`, { headers: authHeaders() });
            toast.success('Template deleted');
            fetchTemplates();
        } catch (e) {
            toast.error('Delete failed');
        }
    };

    // --- Manage-files dialog handlers ---
    const handleAddFile = async () => {
        if (!addFile) { toast.error('Choose a file'); return; }
        setBusyFileId('__adding__');
        try {
            const fd = new FormData();
            fd.append('file', addFile);
            if (addLabel.trim()) fd.append('label', addLabel.trim());
            await axios.post(`${API}/api/artwork-templates/admin/${filesFor.id}/files`, fd, { headers: authHeaders() });
            toast.success('File added');
            setAddFile(null);
            setAddLabel('');
            await fetchTemplates();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Upload failed');
        } finally {
            setBusyFileId(null);
        }
    };

    const handleReplaceFile = async (fileId, newFile) => {
        if (!newFile) return;
        setBusyFileId(fileId);
        try {
            const fd = new FormData();
            fd.append('file', newFile);
            await axios.put(`${API}/api/artwork-templates/admin/${filesFor.id}/files/${fileId}`, fd, { headers: authHeaders() });
            toast.success('File replaced');
            await fetchTemplates();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Replace failed');
        } finally {
            setBusyFileId(null);
        }
    };

    const handleDeleteFile = async (fileId, filename) => {
        if (!window.confirm(`Remove "${filename}" from this template?`)) return;
        setBusyFileId(fileId);
        try {
            await axios.delete(`${API}/api/artwork-templates/admin/${filesFor.id}/files/${fileId}`, { headers: authHeaders() });
            toast.success('File removed');
            await fetchTemplates();
        } catch (e) {
            toast.error('Delete failed');
        } finally {
            setBusyFileId(null);
        }
    };

    const handleDownloadFile = async (tplId, fileId) => {
        try {
            const { data } = await axios.get(`${API}/api/artwork-templates/${tplId}/files/${fileId}/download`, { headers: authHeaders() });
            window.open(data.download_url, '_blank');
        } catch (e) {
            toast.error('Download failed');
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <div className="max-w-7xl mx-auto py-6 px-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900" data-testid="artwork-templates-heading">Artwork Templates</h1>
                    <p className="text-sm text-slate-500 mt-1">Manage reusable print-ready templates. Each template can hold multiple files. Link them to Sales SKUs so they auto-appear on customer orders.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={openManageCats} data-testid="manage-categories-btn">
                        <FolderCog className="h-4 w-4 mr-2" /> Manage categories
                    </Button>
                    <Button onClick={openCreate} data-testid="new-template-btn">
                        <Plus className="h-4 w-4 mr-2" /> New template
                    </Button>
                </div>
            </div>

            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <Input
                                data-testid="templates-search"
                                placeholder="Search by name, description, category…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        <select
                            value={productFilter}
                            onChange={e => setProductFilter(e.target.value)}
                            className="border rounded px-3 py-2 text-sm bg-white h-10"
                            data-testid="templates-filter-product"
                        >
                            <option value="">All product types</option>
                            {PRODUCT_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                        <select
                            value={categoryFilter}
                            onChange={e => setCategoryFilter(e.target.value)}
                            className="border rounded px-3 py-2 text-sm bg-white h-10"
                            data-testid="templates-filter-category"
                        >
                            <option value="">All categories</option>
                            {filterCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </select>
                        <select
                            value={subcategoryFilter}
                            onChange={e => setSubcategoryFilter(e.target.value)}
                            className="border rounded px-3 py-2 text-sm bg-white h-10 disabled:opacity-50"
                            data-testid="templates-filter-subcategory"
                            disabled={subcategoryOptions.length === 0}
                        >
                            <option value="">All subcategories</option>
                            {subcategoryOptions.map(sc => <option key={sc} value={sc}>{sc}</option>)}
                        </select>
                        <label className="flex items-center gap-2 text-sm text-slate-600">
                            <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="templates-show-archived" />
                            Show archived
                        </label>
                        <Button variant="outline" size="sm" onClick={fetchTemplates} title="Refresh">
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="text-center py-10 text-slate-500">Loading…</div>
                    ) : displayed.length === 0 ? (
                        <div className="text-center py-16 text-slate-500">
                            <p className="mb-2">No templates yet.</p>
                            <Button size="sm" variant="outline" onClick={openCreate}>Upload your first template</Button>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {[
                                        { key: 'name', label: 'Name' },
                                        { key: 'product_type', label: 'Product' },
                                        { key: 'category', label: 'Category' },
                                        { key: 'subcategory', label: 'Subcategory' },
                                        { key: 'files', label: 'Files' },
                                        { key: 'size', label: 'Total size' },
                                        { key: 'status', label: 'Status' },
                                    ].map(col => (
                                        <TableHead key={col.key}>
                                            <button
                                                type="button"
                                                onClick={() => toggleSort(col.key)}
                                                className="inline-flex items-center gap-1 hover:text-slate-900 font-medium"
                                                data-testid={`sort-${col.key}`}
                                            >
                                                {col.label}
                                                {sortKey !== col.key
                                                    ? <ArrowUpDown className="h-3 w-3 opacity-40" />
                                                    : (sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                                            </button>
                                        </TableHead>
                                    ))}
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {displayed.map(tpl => {
                                    const files = tpl.files || [];
                                    const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);
                                    return (
                                        <TableRow key={tpl.id} data-testid={`template-row-${tpl.id}`}>
                                            <TableCell className="max-w-[260px]">
                                                <div className="font-medium text-slate-900 truncate" title={tpl.name}>{tpl.name}</div>
                                                {tpl.description && <div className="text-xs text-slate-500 truncate" title={tpl.description}>{tpl.description}</div>}
                                            </TableCell>
                                            <TableCell><Badge variant="secondary">{tpl.product_type?.toUpperCase()}</Badge></TableCell>
                                            <TableCell className="text-sm">
                                                {tpl.category ? <div>{tpl.category}</div> : <span className="text-slate-400">—</span>}
                                            </TableCell>
                                            <TableCell className="text-sm" data-testid={`subcategory-cell-${tpl.id}`}>
                                                {tpl.subcategory ? tpl.subcategory : <span className="text-slate-400">—</span>}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                <button
                                                    className="inline-flex items-center gap-1.5 hover:text-indigo-700"
                                                    onClick={() => setFilesFor(tpl)}
                                                    data-testid={`manage-files-${tpl.id}`}
                                                    title="Manage files"
                                                >
                                                    <Files className="h-4 w-4 text-slate-500" />
                                                    <span className="font-medium">{files.length}</span>
                                                    <span className="text-slate-400">{files.length === 1 ? 'file' : 'files'}</span>
                                                </button>
                                            </TableCell>
                                            <TableCell className="text-xs text-slate-500 tabular-nums">{fmtBytes(totalSize)}</TableCell>
                                            <TableCell>
                                                {tpl.is_archived ? (
                                                    <Badge variant="outline" className="text-slate-500">Archived</Badge>
                                                ) : tpl.is_active ? (
                                                    <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                                                ) : (
                                                    <Badge variant="outline">Inactive</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => setFilesFor(tpl)} title="Manage files" data-testid={`files-${tpl.id}`}>
                                                        <FilePlus className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => openEdit(tpl)} title="Edit metadata" data-testid={`edit-${tpl.id}`}>
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleArchiveToggle(tpl)} title={tpl.is_archived ? 'Unarchive' : 'Archive'} data-testid={`archive-${tpl.id}`}>
                                                        {tpl.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(tpl)} title="Delete" className="text-red-600 hover:bg-red-50" data-testid={`delete-${tpl.id}`}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Create / Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="w-full max-w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="template-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? 'Edit template' : 'New template'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2 min-w-0 break-words">
                        <div>
                            <Label>Name *</Label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder='e.g. LP 12" Center Label' data-testid="template-form-name" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What is this template for?" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="min-w-0">
                                <Label>Product type *</Label>
                                <select value={form.product_type} onChange={e => setForm({ ...form, product_type: e.target.value, category: '' })} className="w-full border rounded px-2 py-2 bg-white text-sm h-10" data-testid="template-form-product">
                                    {PRODUCT_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                            </div>
                            <div className="min-w-0">
                                <Label>Category</Label>
                                <select
                                    value={form.category}
                                    onChange={e => setForm({ ...form, category: e.target.value })}
                                    className="w-full border rounded px-2 py-2 bg-white text-sm h-10"
                                    data-testid="template-form-category"
                                >
                                    <option value="">— None —</option>
                                    {formCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                    {form.category && !formCategories.some(c => c.name === form.category) && (
                                        <option value={form.category}>{form.category} (legacy)</option>
                                    )}
                                </select>
                            </div>
                            <div className="min-w-0">
                                <Label>Subcategory</Label>
                                <Input value={form.subcategory} onChange={e => setForm({ ...form, subcategory: e.target.value })} placeholder="Optional" data-testid="template-form-subcategory" />
                            </div>
                        </div>
                        {/* ============================================================
                            Template files — drag & drop, multi-file, per-file remove.
                            Available in BOTH create and edit modes: on create the files
                            go up with the initial POST; on edit each staged file gets
                            uploaded via /files after the metadata PUT.
                           ============================================================ */}
                        <div>
                            <Label className="break-words">Template files <span className="text-slate-400 font-normal">(PDF, ZIP, AI, EPS, INDD, PSD, PNG, JPG)</span></Label>
                            <div
                                onDragOver={(e) => { e.preventDefault(); setIsDraggingFiles(true); }}
                                onDragLeave={() => setIsDraggingFiles(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setIsDraggingFiles(false);
                                    const dropped = Array.from(e.dataTransfer.files || []);
                                    if (dropped.length === 0) return;
                                    setForm(prev => ({ ...prev, files: [...prev.files, ...dropped] }));
                                }}
                                className={`mt-1 border-2 border-dashed rounded-lg px-4 py-6 text-center transition-colors ${
                                    isDraggingFiles
                                        ? 'border-indigo-400 bg-indigo-50/50'
                                        : 'border-slate-300 hover:border-slate-400 bg-slate-50/40'
                                }`}
                                data-testid="template-files-dropzone"
                            >
                                <FileText className="mx-auto h-6 w-6 text-slate-400 mb-2" />
                                <p className="text-sm text-slate-600">
                                    Drag &amp; drop files here, or{' '}
                                    <label className="text-indigo-600 font-medium cursor-pointer hover:underline">
                                        browse
                                        <input
                                            type="file"
                                            multiple
                                            accept={ACCEPT}
                                            className="hidden"
                                            onChange={(e) => {
                                                const picked = Array.from(e.target.files || []);
                                                setForm(prev => ({ ...prev, files: [...prev.files, ...picked] }));
                                                e.target.value = ''; // allow re-selecting the same file
                                            }}
                                            data-testid="template-form-files"
                                        />
                                    </label>
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                    You can select multiple files at once, or drop them in one at a time.
                                </p>
                            </div>
                            {form.files.length > 0 && (
                                <ul className="mt-2 border rounded-md divide-y bg-white" data-testid="template-files-staged">
                                    {form.files.map((f, i) => (
                                        <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                                            <FileText className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                                            <span className="flex-1 min-w-0 truncate text-slate-700">{f.name}</span>
                                            <span className="text-slate-400">{fmtBytes(f.size)}</span>
                                            <button
                                                type="button"
                                                onClick={() => setForm(prev => ({ ...prev, files: prev.files.filter((_, j) => j !== i) }))}
                                                className="ml-1 p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                                                title="Remove"
                                                aria-label={`Remove ${f.name}`}
                                                data-testid={`template-files-remove-${i}`}
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {editing && (
                                <p className="text-xs text-slate-500 mt-1.5">
                                    These files will be <strong>added</strong> to the template. Existing files stay in place — use &quot;Manage files&quot; from the list to remove old ones.
                                </p>
                            )}
                        </div>

                        {/* ============================================================
                            Preview image — thumbnail preview + one-click remove.
                            Shows the existing preview (if any) when editing so admins
                            can decide to keep, replace or delete it before saving.
                           ============================================================ */}
                        <div>
                            <Label className="break-words">Preview image <span className="text-slate-400 font-normal">(optional PNG, JPG or PDF thumbnail)</span></Label>

                            {/* State A: admin staged a new preview to upload */}
                            {form.preview ? (
                                <div className="mt-1 flex items-center gap-3 rounded-lg border p-2.5 bg-emerald-50/40" data-testid="template-preview-staged">
                                    {form.preview.type?.startsWith('image/') ? (
                                        <img
                                            src={URL.createObjectURL(form.preview)}
                                            alt="Preview"
                                            className="h-14 w-14 object-cover rounded border bg-white"
                                        />
                                    ) : (
                                        <div className="h-14 w-14 flex items-center justify-center rounded border bg-white">
                                            <FileText className="h-6 w-6 text-slate-400" />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-800 truncate">{form.preview.name}</p>
                                        <p className="text-xs text-slate-500">{fmtBytes(form.preview.size)} · will replace existing preview on save</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setForm(prev => ({ ...prev, preview: null }))}
                                        className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors"
                                        title="Remove staged preview"
                                        aria-label="Remove staged preview"
                                        data-testid="template-preview-remove-staged"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : editing && existingPreviewUrl && !form.remove_preview ? (
                                /* State B: existing preview on disk, not yet marked for removal */
                                <div className="mt-1 flex items-center gap-3 rounded-lg border p-2.5 bg-slate-50/60" data-testid="template-preview-existing">
                                    <img
                                        src={existingPreviewUrl}
                                        alt="Existing preview"
                                        className="h-14 w-14 object-cover rounded border bg-white"
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-800 truncate">{editing.preview_filename || 'Current preview'}</p>
                                        <p className="text-xs text-slate-500">Uploaded preview. Replace by dropping a new image, or remove it.</p>
                                    </div>
                                    <label className="px-2.5 py-1 text-xs font-medium rounded border border-slate-300 hover:bg-slate-100 cursor-pointer">
                                        Replace
                                        <input
                                            type="file"
                                            accept="image/*,.pdf"
                                            className="hidden"
                                            onChange={(e) => {
                                                const f = e.target.files?.[0];
                                                if (f) setForm(prev => ({ ...prev, preview: f, remove_preview: false }));
                                                e.target.value = '';
                                            }}
                                            data-testid="template-preview-replace"
                                        />
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setForm(prev => ({ ...prev, remove_preview: true }))}
                                        className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors"
                                        title="Remove preview"
                                        aria-label="Remove preview"
                                        data-testid="template-preview-remove-existing"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : (
                                /* State C: empty (create mode, or removed existing) */
                                <div
                                    onDragOver={(e) => { e.preventDefault(); setIsDraggingPreview(true); }}
                                    onDragLeave={() => setIsDraggingPreview(false)}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        setIsDraggingPreview(false);
                                        const f = e.dataTransfer.files?.[0];
                                        if (f) setForm(prev => ({ ...prev, preview: f, remove_preview: false }));
                                    }}
                                    className={`mt-1 border-2 border-dashed rounded-lg px-4 py-4 text-center transition-colors ${
                                        isDraggingPreview
                                            ? 'border-indigo-400 bg-indigo-50/50'
                                            : 'border-slate-300 hover:border-slate-400 bg-slate-50/40'
                                    }`}
                                    data-testid="template-preview-dropzone"
                                >
                                    <p className="text-sm text-slate-600">
                                        Drop an image here, or{' '}
                                        <label className="text-indigo-600 font-medium cursor-pointer hover:underline">
                                            browse
                                            <input
                                                type="file"
                                                accept="image/*,.pdf"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const f = e.target.files?.[0];
                                                    if (f) setForm(prev => ({ ...prev, preview: f, remove_preview: false }));
                                                    e.target.value = '';
                                                }}
                                                data-testid="template-preview-input"
                                            />
                                        </label>
                                    </p>
                                    {editing && form.remove_preview && (
                                        <p className="text-xs text-red-600 mt-1">
                                            Existing preview will be removed on save.{' '}
                                            <button
                                                type="button"
                                                onClick={() => setForm(prev => ({ ...prev, remove_preview: false }))}
                                                className="underline hover:no-underline"
                                                data-testid="template-preview-remove-undo"
                                            >
                                                Undo
                                            </button>
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="template-form-active" />
                            <Label className="mb-0">Active (visible to customers)</Label>
                        </div>

                        {/* Linked Sales SKUs — reverse many-to-many workflow */}
                        <div className="pt-3 border-t" data-testid="linked-skus-section">
                            <div className="flex items-center justify-between mb-2">
                                <Label className="text-sm font-semibold text-slate-900">Linked Sales SKUs</Label>
                                <span className="text-xs text-slate-500" data-testid="linked-skus-count">
                                    {selectedSkuIds.size} selected
                                </span>
                            </div>
                            <p className="text-xs text-slate-500 mb-3">Pick every Sales SKU that should offer this template. Filters below help narrow the list.</p>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                                <select
                                    value={skuFilterProduct}
                                    onChange={e => { setSkuFilterProduct(e.target.value); setSkuFilterCategory(''); }}
                                    className="w-full min-w-0 border rounded px-2 py-2 text-sm bg-white h-9"
                                    data-testid="sku-filter-product"
                                >
                                    <option value="">All product families</option>
                                    {PRODUCT_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                                <select
                                    value={skuFilterCategory}
                                    onChange={e => setSkuFilterCategory(e.target.value)}
                                    className="w-full min-w-0 border rounded px-2 py-2 text-sm bg-white h-9"
                                    data-testid="sku-filter-category"
                                >
                                    <option value="">All categories</option>
                                    {skuCategoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <div className="relative min-w-0">
                                    <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                    <Input
                                        placeholder="Search SKU or name…"
                                        className="pl-8 h-9 text-sm"
                                        value={skuSearch}
                                        onChange={e => setSkuSearch(e.target.value)}
                                        data-testid="sku-search"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-between mb-1.5">
                                <button
                                    type="button"
                                    onClick={toggleSelectAllFiltered}
                                    disabled={filteredSkus.length === 0}
                                    className="text-xs text-indigo-700 hover:underline disabled:text-slate-400 disabled:no-underline"
                                    data-testid="sku-select-all"
                                >
                                    {allFilteredSelected ? `Deselect all ${filteredSkus.length} filtered` : `Select all ${filteredSkus.length} filtered`}
                                </button>
                                {selectedSkuIds.size > 0 && (
                                    <button
                                        type="button"
                                        onClick={clearSelected}
                                        className="text-xs text-slate-500 hover:text-slate-700"
                                        data-testid="sku-clear-all"
                                    >
                                        Clear selection
                                    </button>
                                )}
                            </div>

                            <div
                                className="border rounded-md max-h-56 overflow-y-auto bg-white"
                                data-testid="sku-list"
                            >
                                {skuLoading ? (
                                    <div className="text-center py-6 text-xs text-slate-500">Loading SKUs…</div>
                                ) : filteredSkus.length === 0 ? (
                                    <div className="text-center py-6 text-xs text-slate-500">No SKUs match the current filter.</div>
                                ) : (
                                    filteredSkus.map(s => {
                                        const checked = selectedSkuIds.has(s.id);
                                        return (
                                            <label
                                                key={s.id}
                                                className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer border-b last:border-b-0 hover:bg-slate-50 ${checked ? 'bg-indigo-50/40' : ''}`}
                                                data-testid={`sku-option-${s.id}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleSku(s.id)}
                                                    data-testid={`sku-checkbox-${s.id}`}
                                                />
                                                <span className="font-mono text-xs text-slate-500 min-w-[70px] shrink-0 truncate" title={s.sales_sku}>{s.sales_sku}</span>
                                                <span className="flex-1 truncate text-slate-800" title={s.display_name}>{s.display_name || '—'}</span>
                                                {s.category && <Badge variant="outline" className="text-[10px] h-4 shrink-0">{s.category}</Badge>}
                                                {!s.product_family && (
                                                    <Badge
                                                        variant="outline"
                                                        className="text-[10px] h-4 shrink-0 text-amber-700 border-amber-300"
                                                        title="This SKU has no product family set — showing under every family filter"
                                                    >
                                                        no family
                                                    </Badge>
                                                )}
                                                {!s.is_active && <Badge variant="outline" className="text-[10px] h-4 text-slate-400 shrink-0">inactive</Badge>}
                                            </label>
                                        );
                                    })
                                )}
                            </div>

                            {selectedSkuIds.size > 0 && (
                                <div className="mt-3" data-testid="selected-skus-chips">
                                    <Label className="text-xs text-slate-500">Selected ({selectedSkuIds.size})</Label>
                                    <div className="flex flex-wrap gap-1.5 mt-1 max-h-24 overflow-y-auto">
                                        {Array.from(selectedSkuIds).map(id => {
                                            const s = skuOptions.find(x => x.id === id);
                                            if (!s) return null;
                                            return (
                                                <span
                                                    key={id}
                                                    className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full"
                                                    data-testid={`selected-chip-${id}`}
                                                >
                                                    <span className="font-mono">{s.sales_sku}</span>
                                                    <span className="text-indigo-500">·</span>
                                                    <span className="truncate max-w-[140px]" title={s.display_name}>{s.display_name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeSelected(id)}
                                                        className="ml-0.5 hover:text-indigo-950"
                                                        data-testid={`chip-remove-${id}`}
                                                        aria-label="Remove"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSubmit} disabled={saving} data-testid="template-form-save">
                            {saving ? 'Saving…' : (editing ? 'Save changes' : 'Create')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Manage files dialog */}
            <Dialog open={!!filesFor} onOpenChange={(o) => { if (!o) { setFilesFor(null); setAddFile(null); setAddLabel(''); } }}>
                <DialogContent className="max-w-2xl" data-testid="files-dialog">
                    <DialogHeader>
                        <DialogTitle>Files — {filesFor?.name}</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        {/* Existing files */}
                        <div className="border rounded-md divide-y">
                            {(filesFor?.files || []).length === 0 ? (
                                <div className="text-center py-6 text-sm text-slate-500">No files yet — add one below.</div>
                            ) : (
                                (filesFor?.files || []).map(f => (
                                    <div key={f.id} className="p-3 flex items-center gap-3" data-testid={`file-row-${f.id}`}>
                                        <FileText className="h-5 w-5 text-slate-400 shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-slate-900 truncate" title={f.filename}>{f.filename}</div>
                                            <div className="text-[11px] text-slate-500 flex items-center gap-2">
                                                <Badge variant="outline" className="text-[9px] uppercase h-4">{f.file_extension || 'file'}</Badge>
                                                <span>{fmtBytes(f.size)}</span>
                                                {f.label && <span>· {f.label}</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Button
                                                variant="ghost" size="sm" title="Download"
                                                onClick={() => handleDownloadFile(filesFor.id, f.id)}
                                                data-testid={`file-download-${f.id}`}
                                            >
                                                <Download className="h-4 w-4" />
                                            </Button>
                                            <label
                                                className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-slate-100 cursor-pointer"
                                                title="Replace file"
                                                data-testid={`file-replace-label-${f.id}`}
                                            >
                                                <RefreshCw className={`h-4 w-4 ${busyFileId === f.id ? 'animate-spin text-indigo-600' : ''}`} />
                                                <input
                                                    type="file"
                                                    accept={ACCEPT}
                                                    className="hidden"
                                                    onChange={e => handleReplaceFile(f.id, e.target.files?.[0])}
                                                    data-testid={`file-replace-${f.id}`}
                                                />
                                            </label>
                                            <Button
                                                variant="ghost" size="sm" title="Remove"
                                                className="text-red-600 hover:bg-red-50"
                                                onClick={() => handleDeleteFile(f.id, f.filename)}
                                                data-testid={`file-delete-${f.id}`}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Add-file panel */}
                        <div className="border rounded-md p-3 bg-slate-50">
                            <Label className="text-xs uppercase text-slate-500 tracking-wide">Add a file</Label>
                            <div className="mt-2 grid grid-cols-[1fr_auto] gap-2 items-end">
                                <div>
                                    <Input
                                        type="file"
                                        accept={ACCEPT}
                                        onChange={e => setAddFile(e.target.files?.[0] || null)}
                                        data-testid="add-file-input"
                                    />
                                    <Input
                                        className="mt-2"
                                        placeholder="Optional label (e.g. Front cover, Inner sleeve)"
                                        value={addLabel}
                                        onChange={e => setAddLabel(e.target.value)}
                                        data-testid="add-file-label"
                                    />
                                </div>
                                <Button
                                    onClick={handleAddFile}
                                    disabled={!addFile || busyFileId === '__adding__'}
                                    data-testid="add-file-submit"
                                >
                                    {busyFileId === '__adding__' ? 'Uploading…' : (<><Plus className="h-4 w-4 mr-1" />Add</>)}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFilesFor(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Manage Categories dialog */}
            <Dialog open={manageCatOpen} onOpenChange={(o) => { setManageCatOpen(o); if (!o) { setEditingCatId(null); setNewCatName(''); } }}>
                <DialogContent className="w-full max-w-[calc(100vw-2rem)] sm:max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="manage-categories-dialog">
                    <DialogHeader>
                        <DialogTitle>Manage categories</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label className="text-xs text-slate-500">Product type</Label>
                            <select
                                value={manageProduct}
                                onChange={e => { setManageProduct(e.target.value); setEditingCatId(null); loadManageCats(e.target.value); }}
                                className="w-full border rounded px-2 py-2 bg-white text-sm h-10 mt-1"
                                data-testid="manage-cat-product"
                            >
                                {PRODUCT_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                        </div>

                        {/* Add new */}
                        <div className="flex items-end gap-2">
                            <div className="flex-1 min-w-0">
                                <Label className="text-xs text-slate-500">New category</Label>
                                <Input
                                    value={newCatName}
                                    onChange={e => setNewCatName(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') createCat(); }}
                                    placeholder="e.g. Spine Label"
                                    className="mt-1"
                                    data-testid="manage-cat-new-input"
                                />
                            </div>
                            <Button onClick={createCat} disabled={catBusy || !newCatName.trim()} data-testid="manage-cat-add-btn">
                                <Plus className="h-4 w-4 mr-1" /> Add
                            </Button>
                        </div>

                        {/* List */}
                        <div className="border rounded-md divide-y" data-testid="manage-cat-list">
                            {manageCatLoading ? (
                                <div className="text-center py-6 text-sm text-slate-500">Loading…</div>
                            ) : manageCats.length === 0 ? (
                                <div className="text-center py-6 text-sm text-slate-500">No categories yet.</div>
                            ) : (
                                manageCats.map((cat, idx) => (
                                    <div key={cat.id} className={`flex items-center gap-2 px-3 py-2 ${cat.is_archived ? 'bg-slate-50/60' : ''}`} data-testid={`manage-cat-row-${cat.id}`}>
                                        <div className="flex flex-col shrink-0">
                                            <button type="button" onClick={() => moveCat(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move up" data-testid={`manage-cat-up-${cat.id}`}>
                                                <ChevronUp className="h-3.5 w-3.5" />
                                            </button>
                                            <button type="button" onClick={() => moveCat(idx, 1)} disabled={idx === manageCats.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move down" data-testid={`manage-cat-down-${cat.id}`}>
                                                <ChevronDown className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            {editingCatId === cat.id ? (
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        value={editingCatName}
                                                        onChange={e => setEditingCatName(e.target.value)}
                                                        onKeyDown={e => { if (e.key === 'Enter') saveRenameCat(cat.id); if (e.key === 'Escape') setEditingCatId(null); }}
                                                        className="h-8 text-sm"
                                                        autoFocus
                                                        data-testid={`manage-cat-edit-input-${cat.id}`}
                                                    />
                                                    <Button size="sm" onClick={() => saveRenameCat(cat.id)} disabled={catBusy} data-testid={`manage-cat-save-${cat.id}`}>
                                                        <Check className="h-4 w-4" />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => setEditingCatId(null)}>
                                                        <X className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="text-sm font-medium text-slate-800 truncate" title={cat.name}>{cat.name}</span>
                                                    {cat.is_archived && <Badge variant="outline" className="text-[10px] h-4 text-slate-400 shrink-0">archived</Badge>}
                                                    {cat.in_use && <Badge variant="outline" className="text-[10px] h-4 shrink-0">in use</Badge>}
                                                </div>
                                            )}
                                        </div>
                                        {editingCatId !== cat.id && (
                                            <div className="flex items-center gap-1 shrink-0">
                                                <Button variant="ghost" size="sm" title="Rename" onClick={() => { setEditingCatId(cat.id); setEditingCatName(cat.name); }} data-testid={`manage-cat-rename-${cat.id}`}>
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" title={cat.is_archived ? 'Unarchive' : 'Archive'} onClick={() => archiveCatToggle(cat)} data-testid={`manage-cat-archive-${cat.id}`}>
                                                    {cat.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                                </Button>
                                                <Button
                                                    variant="ghost" size="sm"
                                                    title={cat.in_use ? 'In use — cannot delete' : 'Delete'}
                                                    onClick={() => deleteCat(cat)}
                                                    disabled={cat.in_use}
                                                    className="text-red-600 hover:bg-red-50 disabled:text-slate-300"
                                                    data-testid={`manage-cat-delete-${cat.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                        <p className="text-xs text-slate-500">
                            Renaming a category updates every template that uses it. Categories in use can be archived (hidden from pickers) but not deleted.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setManageCatOpen(false)}>Done</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            </div>
        </div>
    );
}
